/**
 * P8-01A · i18n foundation — the locale model.
 *
 * The URL carries a bare **language** segment (`/en/`, `/fr/`) per the P8-DB02
 * URL model, while **region** is a separate axis (P8-DB01: language × currency ×
 * region). The `Intl` formatting locale is *derived* from the pair — language
 * `en` + region `GB` → `en-GB` — so a shopper reading English from France still
 * gets French number/date conventions without needing an `en-FR` catalogue.
 *
 * That split is why `Store.enabledLocales` holds bare languages (`["en","fr"]`)
 * while `Store.defaultLocale` stays a full BCP-47 tag (`"en-GB"`): the former
 * drives routing and catalogues, the latter seeds the default language *and*
 * region in one value.
 *
 * Client-safe and pure — no server-only imports, so the switcher (P8-04) can
 * reuse these helpers directly.
 */

/**
 * Header carrying the language the edge middleware parsed out of the path.
 * Middleware runs on the Edge runtime and can't reach Prisma, so it forwards a
 * *candidate* and the Node layer validates it against `Store.enabledLocales` —
 * the same forward-then-resolve split ADR-0001 uses for the store host.
 */
export const LOCALE_HEADER = "x-vx-locale";

/** Cookie holding the shopper's explicit language override (P8-DB01). */
export const LOCALE_COOKIE = "NEXT_LOCALE";

/** Cookie holding the shopper's explicit region override (P8-DB01). */
export const REGION_COOKIE = "vx_region";

/** Cookie holding the shopper's explicit currency override (P8-05A). */
export const CURRENCY_COOKIE = "vx_currency";

/** Persisted-preference cookie lifetime — 1 year, per P8-DB01. */
export const LOCALE_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

/** A language code — the URL segment and message-catalogue key (ISO-639-1). */
export type Language = string;

/** An ISO-3166 alpha-2 region code, uppercase. */
export type Region = string;

/** Text direction for a language. */
export type Direction = "ltr" | "rtl";

/**
 * Languages the *template* knows how to render, with their writing direction
 * and endonym (the language's own name — what a switcher should display).
 *
 * This is a linguistic registry, not store config: which of these a given store
 * actually offers comes from `Store.enabledLocales`. Adding a language here
 * makes it *available* to stores; it does not enable it for anyone.
 */
export const SUPPORTED_LANGUAGES: Readonly<
  Record<Language, { endonym: string; dir: Direction }>
> = {
  en: { endonym: "English", dir: "ltr" },
  fr: { endonym: "Français", dir: "ltr" },
  de: { endonym: "Deutsch", dir: "ltr" },
  es: { endonym: "Español", dir: "ltr" },
  it: { endonym: "Italiano", dir: "ltr" },
  nl: { endonym: "Nederlands", dir: "ltr" },
  ar: { endonym: "العربية", dir: "rtl" },
  he: { endonym: "עברית", dir: "rtl" },
} as const;

/**
 * Language → writing direction. Drives `<html dir>` and the RTL layout flip
 * (P8-DB01 §06). Unknown languages default to `ltr`.
 */
export const LOCALE_DIRECTION: Readonly<Record<Language, Direction>> =
  Object.fromEntries(
    Object.entries(SUPPORTED_LANGUAGES).map(([lang, meta]) => [lang, meta.dir]),
  );

/** The template-wide fallback when a store has no usable locale config. */
export const FALLBACK_LANGUAGE: Language = "en";

/** True when the template has a catalogue and direction for this language. */
export function isSupportedLanguage(value: string | null | undefined): boolean {
  return value != null && Object.hasOwn(SUPPORTED_LANGUAGES, value);
}

/**
 * Normalise any locale-ish input to a bare lowercase language code.
 * `"en-GB"` → `"en"`, `"EN"` → `"en"`, `"en_GB"` → `"en"`.
 */
export function languageOf(tag: string | null | undefined): Language | null {
  if (!tag) return null;
  const [lang] = tag.trim().toLowerCase().split(/[-_]/);
  return lang ? lang : null;
}

/**
 * Pull the region out of a BCP-47 tag, uppercased. `"en-GB"` → `"GB"`.
 * Returns `null` for a bare language (`"en"`) — the caller decides the default.
 */
export function regionOf(tag: string | null | undefined): Region | null {
  if (!tag) return null;
  const parts = tag.trim().split(/[-_]/);
  const region = parts[1];
  // Only a 2-letter alpha region is meaningful here; ignore script subtags
  // ("zh-Hans") and numeric UN M.49 codes, which we don't model.
  return region && /^[A-Za-z]{2}$/.test(region) ? region.toUpperCase() : null;
}

/** Writing direction for a language, defaulting to `ltr` for unknowns. */
export function directionOf(language: string | null | undefined): Direction {
  const lang = languageOf(language);
  return (lang ? LOCALE_DIRECTION[lang] : undefined) ?? "ltr";
}

/**
 * Build the `Intl` formatting locale from the resolved pair. A region always
 * wins when present, because it — not the language — decides number grouping,
 * date order, and currency placement.
 */
export function toIntlLocale(language: string, region?: string | null): string {
  const lang = languageOf(language) ?? FALLBACK_LANGUAGE;
  const reg = region ? region.trim().toUpperCase() : null;
  // P11-19 · Only a well-formed BCP-47 region subtag may be appended: two
  // letters, or three digits (UN M49). The region axis now carries a merchant
  // `Region.code` — "UK", "EU", but equally "ROW" or "APAC" — and those are
  // NOT region subtags. `Intl.NumberFormat("en-ROW")` throws a RangeError, so
  // passing one through would crash every page that formats money, which is
  // every page. An unusable code degrades to the bare language instead.
  return reg && /^([A-Z]{2}|[0-9]{3})$/.test(reg) ? `${lang}-${reg}` : lang;
}

/**
 * The store's enabled languages, normalised and de-duplicated, always
 * containing the store default. Legacy rows that still hold BCP-47 tags
 * (`["en-US","en-GB"]`) collapse to their languages (`["en"]`), so this is safe
 * to call against un-migrated data.
 */
export function enabledLanguagesOf(store: {
  defaultLocale: string;
  enabledLocales: string[];
}): Language[] {
  const defaultLang = languageOf(store.defaultLocale) ?? FALLBACK_LANGUAGE;
  const langs = store.enabledLocales
    .map((tag) => languageOf(tag))
    .filter(
      (lang): lang is Language => lang != null && isSupportedLanguage(lang),
    );

  // The default is always offerable, even if config forgot to list it.
  return [...new Set([defaultLang, ...langs])];
}

/**
 * P8-05A · The currencies a shopper may switch the DISPLAY to.
 *
 * Mirrors `enabledLanguagesOf`: the base currency is always offerable even if
 * config forgot to list it, so `enabledCurrencies: []` means "base only" and a
 * single-currency store needs no configuration at all.
 */
export function enabledCurrenciesOf(store: {
  baseCurrency: string;
  enabledCurrencies: string[];
}): string[] {
  const base = store.baseCurrency.trim().toUpperCase();
  const extra = store.enabledCurrencies
    .map((c) => c.trim().toUpperCase())
    .filter((c) => /^[A-Z]{3}$/.test(c));
  return [...new Set([base, ...extra])];
}

/** Where a resolved display currency came from. */
export type CurrencySource = "cookie" | "account" | "geo" | "default";

/**
 * P8-05A · Resolve the display currency for a request.
 *
 * Precedence is **cookie ▸ account ▸ geo ▸ base**.
 *
 * - **No `path` axis.** Currency is a personalisation, not a canonical URL
 *   variant. Putting it in the path would fork every URL P8-07 just spent a
 *   ticket canonicalising, and split each page's search-engine authority.
 * - **No `Accept-Language` axis.** That is a language signal, not a currency
 *   one — an en-GB browser in Dublin wants EUR.
 * - **`geo` is a DEFAULT, never an override** (owner decision, 3 Aug 2026).
 *   P8-DB01 held that nothing changing a displayed price may auto-apply, and
 *   P11-DB01 shipped geo as a prompt. That is now relaxed for the *unset* case
 *   only: a first-time visitor from a country the store has a region for is
 *   served that region's currency instead of base. It sits BELOW cookie and
 *   account, so a shopper who has ever chosen is never overridden — travelling
 *   or using a VPN cannot silently reprice someone who made a choice. A
 *   visitor from a country no region covers falls through to base, unchanged.
 *
 * A currency the store does not offer is ignored rather than erroring, exactly
 * as `resolveLocale` ignores a disabled language: a stale cookie from a
 * de-listed currency should quietly fall back.
 */
export function resolveCurrency(input: {
  store: { baseCurrency: string; enabledCurrencies: string[] };
  cookieCurrency?: string | null;
  accountCurrency?: string | null;
  /** The suggested currency of the region the visitor's IP falls in, if any. */
  geoCurrency?: string | null;
}): { currency: string; source: CurrencySource; rejected?: string } {
  const enabled = enabledCurrenciesOf(input.store);
  const base = enabled[0]!;

  const candidates: Array<[CurrencySource, string | null | undefined]> = [
    ["cookie", input.cookieCurrency],
    ["account", input.accountCurrency],
    ["geo", input.geoCurrency],
  ];

  // P11-20 · Report a stored preference we refused to honour.
  //
  // Falling back silently is safe — it always has been — but it is not
  // *honest*: a shopper who chose EUR last week, and whose store has since
  // de-listed it, sees GBP prices with nothing to say why. From their side that
  // is indistinguishable from a pricing bug. Only well-formed codes count, so
  // a corrupted cookie stays silent rather than naming garbage at the shopper.
  let rejected: string | undefined;

  for (const [source, raw] of candidates) {
    const code = raw?.trim().toUpperCase();
    if (!code) continue;
    if (enabled.includes(code)) {
      return rejected
        ? { currency: code, source, rejected }
        : { currency: code, source };
    }
    if (!rejected && /^[A-Z]{3}$/.test(code)) rejected = code;
  }

  return rejected
    ? { currency: base, source: "default", rejected }
    : { currency: base, source: "default" };
}

/** A fully-resolved locale — everything downstream formatting needs. */
export interface ResolvedLocale {
  /** Bare language: the URL segment and catalogue key. */
  language: Language;
  /** Region driving formatting conventions; `null` when the store sets none. */
  region: Region | null;
  /** Derived BCP-47 tag for `Intl` (`en-GB`). */
  intlLocale: string;
  /** Writing direction for `<html dir>`. */
  dir: Direction;
}

/**
 * Where a resolved language came from. Drives the resolver's own precedence and
 * the `hreflang`/canonical decisions — it is NOT an analytics dimension: the
 * `i18n.locale.resolved` event this once referenced was never emitted and was
 * dropped in P11-23.
 */
export type LocaleSource = "path" | "cookie" | "account" | "header" | "default";

/**
 * Resolve the active locale for a request.
 *
 * Precedence is the P8-DB01 model — an explicit choice outranks a detected one:
 *   1. `path`   — an explicit `/{lang}/` segment (a shared or bookmarked URL)
 *   2. `cookie` — the shopper's persisted override (`NEXT_LOCALE`)
 *   3. `header` — `Accept-Language` detection (a suggestion, never an override)
 *   4. `default` — the store default
 *
 * Anything not enabled for the store is ignored rather than erroring, so an
 * unknown or disabled segment degrades to the default instead of 500-ing.
 */
export function resolveLocale(input: {
  store: { defaultLocale: string; enabledLocales: string[] };
  pathLanguage?: string | null;
  cookieLanguage?: string | null;
  /**
   * P8-04 · The signed-in shopper's stored choice. Ranked BELOW the cookie on
   * purpose: the cookie is only ever written by an explicit choice on this
   * device, so it represents the more recent deliberate act. It ranks above
   * `Accept-Language` because a stored choice is deliberate and a browser
   * header is only a hint.
   */
  accountLanguage?: string | null;
  acceptLanguage?: string | null;
  /** Explicit region override (P8-04's `vx_region` cookie); falls back to the store default. */
  region?: string | null;
  /**
   * P11-19 · The region the visitor's IP falls in, used only when there is no
   * explicit choice. A DEFAULT, never an override — see `resolveCurrency`.
   */
  geoRegion?: string | null;
}): ResolvedLocale & { source: LocaleSource } {
  const enabled = enabledLanguagesOf(input.store);
  const isEnabled = (lang: Language | null): lang is Language =>
    lang != null && enabled.includes(lang);

  const candidates: Array<[LocaleSource, Language | null]> = [
    ["path", languageOf(input.pathLanguage)],
    ["cookie", languageOf(input.cookieLanguage)],
    ["account", languageOf(input.accountLanguage)],
    ["header", pickFromAcceptLanguage(input.acceptLanguage, enabled)],
  ];

  let source: LocaleSource = "default";
  let language: Language =
    languageOf(input.store.defaultLocale) ?? FALLBACK_LANGUAGE;

  for (const [candidateSource, candidate] of candidates) {
    if (isEnabled(candidate)) {
      source = candidateSource;
      language = candidate;
      break;
    }
  }

  // P11-19 (owner decision, 3 Aug 2026) · cookie/account ▸ geo ▸ store default.
  // `input.region` is the explicit choice; `geoRegion` is what the visitor's IP
  // resolves to. Geo sits below the explicit value so a shopper who has chosen
  // is never repriced by travelling, and above the store default so a first
  // visit from a covered country lands in the right market.
  const region =
    (input.region ? input.region.trim().toUpperCase() : null) ??
    (input.geoRegion ? input.geoRegion.trim().toUpperCase() : null) ??
    regionOf(input.store.defaultLocale);

  return {
    language,
    region,
    intlLocale: toIntlLocale(language, region),
    dir: directionOf(language),
    source,
  };
}

/**
 * Best enabled language from an `Accept-Language` header, honouring q-weights.
 * `"fr-CA,fr;q=0.9,en;q=0.8"` → `"fr"` when French is enabled, else `"en"`.
 */
export function pickFromAcceptLanguage(
  header: string | null | undefined,
  enabled: readonly Language[],
): Language | null {
  if (!header) return null;

  const ranked = header
    .split(",")
    .map((part) => {
      const [tag, ...params] = part.trim().split(";");
      const q = params
        .map((p) => /^q=([\d.]+)$/.exec(p.trim())?.[1])
        .find((v) => v != null);
      return { language: languageOf(tag), q: q != null ? Number(q) : 1 };
    })
    .filter((entry) => entry.language != null && Number.isFinite(entry.q))
    // Stable sort by descending q — ties keep header order, which is the
    // client's own stated preference.
    .sort((a, b) => b.q - a.q);

  return (
    ranked.find((entry) => enabled.includes(entry.language!))?.language ?? null
  );
}
